import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  saveAttendance,
  listAttendance,
  deleteAttendance,
  getAttendanceForClassDate,
} from '../api/attendanceApi';
import { listStudentsByClass } from '../api/studentsApi';
import SearchableSelect from './SearchableSelect';
import { classOptions, formatClassLabel } from '../constants/classes';
import { matchesSearch } from '../utils/searchFilter';

export default function CoordinatorAttendanceSection({ setMsg, setErr, allowDelete = true }) {
  const today = new Date().toISOString().slice(0, 10);
  const [attForm, setAttForm] = useState({ date: today, className: '' });
  const [studentsInClass, setStudentsInClass] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [presence, setPresence] = useState({});
  const [attStudentFilter, setAttStudentFilter] = useState('');
  const [attTableFilter, setAttTableFilter] = useState('');
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [deletingAttId, setDeletingAttId] = useState('');

  const classOpts = useMemo(() => classOptions(), []);
  const classOptsWithEmpty = useMemo(
    () => [{ value: '', label: 'Select class (NUR–8)…' }, ...classOpts],
    [classOpts]
  );

  const refreshAttendance = useCallback(async () => {
    const att = await listAttendance();
    setAttendanceRows(att.sort((a, b) => (b.date || '').localeCompare(a.date || '')));
  }, []);

  useEffect(() => {
    refreshAttendance().catch((e) => setErr(e.message));
  }, [refreshAttendance, setErr]);

  useEffect(() => {
    const className = String(attForm.className || '').trim();
    if (!className) {
      setStudentsInClass([]);
      setPresence({});
      setStudentsLoading(false);
      return;
    }
    let cancelled = false;
    setStudentsLoading(true);
    (async () => {
      try {
        const [list, existing] = await Promise.all([
          listStudentsByClass(className),
          getAttendanceForClassDate(attForm.date, className),
        ]);
        if (cancelled) return;
        setStudentsInClass(list);
        if (existing) {
          const presentSet = new Set((existing.presentStudents || []).map(String));
          setPresence(
            Object.fromEntries(
              list.map((s) => [s.studentId, presentSet.size === 0 ? true : presentSet.has(String(s.studentId))])
            )
          );
        } else {
          setPresence(Object.fromEntries(list.map((s) => [s.studentId, true])));
        }
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attForm.className, attForm.date, setErr]);

  const filteredStudentsForAtt = useMemo(
    () => studentsInClass.filter((s) => matchesSearch(attStudentFilter, [s.studentName, s.studentId])),
    [studentsInClass, attStudentFilter]
  );

  const filteredAttRows = useMemo(
    () =>
      attendanceRows.filter((r) =>
        matchesSearch(attTableFilter, [r.date, r.class, (r.presentStudents || []).join(', ')])
      ),
    [attendanceRows, attTableFilter]
  );

  const handleAttendance = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      const presentIds = studentsInClass.filter((s) => presence[s.studentId] !== false).map((s) => s.studentId);
      const absentIds = studentsInClass.filter((s) => presence[s.studentId] === false).map((s) => s.studentId);
      const count = await saveAttendance({ ...attForm, presentStudents: presentIds, absentStudents: absentIds });
      await refreshAttendance();
      setMsg(
        `Attendance saved for class ${formatClassLabel(attForm.className)}: ${count} present, ${studentsInClass.length - count} absent.`
      );
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handleDeleteAttendance = async (row) => {
    const label = `${row.date || '—'} · class ${row.class || '—'}`;
    if (!window.confirm(`Delete attendance for ${label}?`)) return;
    setMsg('');
    setErr('');
    setDeletingAttId(row.id);
    try {
      await deleteAttendance(row.id);
      await refreshAttendance();
      setMsg(`Deleted attendance: ${label}`);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setDeletingAttId('');
    }
  };

  return (
    <>
      <div className="mps-card">
        <h2>Mark / edit attendance</h2>
        <p className="mps-muted" style={{ marginTop: 0 }}>
          Select class and date. Checked = present, unchecked = absent. Saving updates existing records for that day.
        </p>
        <form onSubmit={handleAttendance}>
          <div className="mps-grid">
            <div className="mps-field">
              <label>Date</label>
              <input type="date" value={attForm.date} onChange={(e) => setAttForm({ ...attForm, date: e.target.value })} required />
            </div>
            <div className="mps-field">
              <label>Class</label>
              <SearchableSelect
                value={attForm.className}
                onChange={(v) => setAttForm({ ...attForm, className: v })}
                options={classOptsWithEmpty}
                placeholder="Select class…"
              />
            </div>
          </div>
          {attForm.className ? (
            <>
              {studentsLoading ? (
                <p className="mps-muted">Loading students…</p>
              ) : studentsInClass.length === 0 ? (
                <p className="mps-muted">No students found for this class.</p>
              ) : (
                <>
                  <div className="mps-att-bulk-actions" style={{ marginTop: 12 }}>
                    <input
                      type="search"
                      className="mps-table-filter"
                      value={attStudentFilter}
                      onChange={(e) => setAttStudentFilter(e.target.value)}
                      placeholder="Filter students…"
                    />
                    <button
                      type="button"
                      className="mps-btn mps-btn-secondary mps-btn-sm"
                      onClick={() => setPresence(Object.fromEntries(studentsInClass.map((s) => [s.studentId, true])))}
                    >
                      Check all
                    </button>
                    <button
                      type="button"
                      className="mps-btn mps-btn-secondary mps-btn-sm"
                      onClick={() => setPresence(Object.fromEntries(studentsInClass.map((s) => [s.studentId, false])))}
                    >
                      Uncheck all
                    </button>
                  </div>
                  <ul className="mps-attendance-list">
                    {filteredStudentsForAtt.map((s) => {
                      const inputId = `coord-att-${s.studentId}`;
                      const isPresent = presence[s.studentId] !== false;
                      return (
                        <li key={s.studentId} className="mps-att-row">
                          <input
                            id={inputId}
                            type="checkbox"
                            className="mps-att-cb"
                            checked={isPresent}
                            onChange={(e) => setPresence((prev) => ({ ...prev, [s.studentId]: e.target.checked }))}
                          />
                          <label htmlFor={inputId} className="mps-att-row-name">
                            {s.studentName} <span className="mps-muted">({s.studentId})</span>
                          </label>
                          <span className={`mps-att-status ${isPresent ? 'mps-att-status-present' : 'mps-att-status-absent'}`}>
                            {isPresent ? 'Present' : 'Absent'}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <button type="submit" className="mps-btn" style={{ marginTop: 12 }}>
                    Save attendance
                  </button>
                </>
              )}
            </>
          ) : null}
        </form>
      </div>
      <div className="mps-card">
        <h2>Attendance history</h2>
        <input
          type="search"
          className="mps-table-filter"
          value={attTableFilter}
          onChange={(e) => setAttTableFilter(e.target.value)}
          placeholder="Search date, class…"
        />
        <table className="mps-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Class</th>
              <th>Present</th>
              <th>Absent</th>
              {allowDelete ? <th>Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {filteredAttRows.length === 0 ? (
              <tr>
                <td colSpan={allowDelete ? 5 : 4} className="mps-muted">
                  No records yet.
                </td>
              </tr>
            ) : (
              filteredAttRows.slice(0, 30).map((row) => (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td>{row.class}</td>
                  <td>{(row.presentStudents || []).length}</td>
                  <td>{(row.absentStudents || []).length}</td>
                  {allowDelete ? (
                    <td>
                      <button
                        type="button"
                        className="mps-btn mps-btn-sm mps-btn-danger"
                        disabled={deletingAttId === row.id}
                        onClick={() => handleDeleteAttendance(row)}
                      >
                        {deletingAttId === row.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
